import { AcceptedIntentSchema, type AcceptedIntent, type Continuity, type RunState } from '@dissidium/contracts';
import { SaveCorruptError, StaleWriterError, StorageUnavailableError, WriterConflictError } from '../errors.ts';
import { applyOperation, parseOperation, validateJournal, type DurableOperation, type JournalRecord } from '../journal/index.ts';
import { SingleWriterLock, LEASE_KEY } from '../ownership-lock/lock.ts';
import { parseLease } from '../ownership-lock/lease.ts';
import { benchCodec, decodeCheckpoint, encodeCheckpoint, type StateCodec, type RollingState } from './codec.ts';
import { canonical } from '@dissidium/sim';
import { isQuotaError, STORES, type StorageDriver, type StorageTransaction } from './driver.ts';

let frameStarted = 0;
/** A new MessageChannel task alone does not give the renderer a turn: Chrome can
 * drain every save phase in one frame. After 4 ms, resume after an animation
 * callback instead. Hidden pages use ordinary tasks; the timeout also completes
 * a pending yield if the page becomes hidden while awaiting its frame. Nothing
 * yields inside an IndexedDB transaction, and publication still follows commit. */
export const nextTask = (): Promise<void> => {
  if (typeof MessageChannel === 'undefined' || typeof document === 'undefined' || typeof requestAnimationFrame === 'undefined') return new Promise(resolve => setImmediate(resolve));
  const channel = new MessageChannel();
  return new Promise(resolve => {
    channel.port1.onmessage = () => { channel.port1.close(); channel.port2.close(); resolve(); };
    if (document.hidden || performance.now() - frameStarted < 4) { channel.port2.postMessage(null); return; }
    let frame = 0;
    const resume = (): void => { clearTimeout(timeout); cancelAnimationFrame(frame); channel.port2.postMessage(null); };
    const timeout = setTimeout(resume, 50);
    frame = requestAnimationFrame(() => { frameStarted = performance.now(); resume(); });
  });
};

/** Every point where an interruption can land, named so a test can interrupt each one. */
export type WriteBoundary =
  | 'begin:before-write' | 'begin:in-transaction' | 'begin:after-write'
  | 'intent:before-write' | 'intent:in-transaction' | 'intent:after-write'
  | 'death:before-write' | 'death:in-transaction' | 'death:after-write'
  | 'rotate:after-checkpoint' | 'rotate:after-commit';

export const WRITE_BOUNDARIES: readonly WriteBoundary[] = [
  'begin:before-write', 'begin:in-transaction', 'begin:after-write',
  'intent:before-write', 'intent:in-transaction', 'intent:after-write',
  'death:before-write', 'death:in-transaction', 'death:after-write',
  'rotate:after-checkpoint', 'rotate:after-commit',
];

export type RollingModel<S, O> = StateCodec<S> & { parseOperation: (input: unknown) => O; apply: (state: S, operation: O) => S; upgrade?: (state: S) => S };
const benchModel: RollingModel<RunState, DurableOperation> = { ...benchCodec, parseOperation, apply: applyOperation };

export type DurableStoreOptions<S extends RollingState = RunState, O = DurableOperation> = Readonly<{
  driver: StorageDriver;
  model?: RollingModel<S, O>;
  snapshotEvery?: number;
  chronologyId: string;
  namespace: Continuity['namespace'];
  holderId: string;
  leaseTtlMs?: number;
  now?: () => number;
  interrupt?: (boundary: WriteBoundary) => void;
  /** Told once, before that wait begins, so a host can say the previous window is letting go. */
  onLeaseWait?: (wait: LeaseWait) => void;
}>;

/** A reopen must give up its wait to the player's patience, never to a window that is still playing. */
export type LeaseWait = Readonly<{ waitMs: number; holderId: string | null }>;

/** Added to a lapsed lease before retrying, so a clock that rounded down cannot lose the race again. */
const LEASE_WAIT_MARGIN_MS = 200;
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

type Pointer = Readonly<{
  version: 1;
  chronologyId: string;
  namespace: Continuity['namespace'];
  checkpointId: string;
  through: number;
  nextSequence: number;
  updatedAtMs: number;
}>;

const POINTER_KEY = 'pointer';
const journalKey = (sequence: number): string => `seq:${String(sequence).padStart(12, '0')}`;
const keySequence = (key: string): number => {
  const value = Number(key.slice('seq:'.length));
  if (!Number.isSafeInteger(value) || value < 0) throw new SaveCorruptError(`The rolling journal holds an unreadable key: ${key}`);
  return value;
};

function parsePointer(input: unknown): Pointer {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) throw new SaveCorruptError('The canonical save pointer is missing or unreadable');
  const candidate = input as Record<string, unknown>;
  if (candidate.version !== 1 || typeof candidate.chronologyId !== 'string' || typeof candidate.namespace !== 'string' ||
      typeof candidate.checkpointId !== 'string' || !candidate.checkpointId || !Number.isSafeInteger(candidate.through) ||
      !Number.isSafeInteger(candidate.nextSequence) || (candidate.through as number) < 0 ||
      (candidate.nextSequence as number) < (candidate.through as number)) {
    throw new SaveCorruptError('The canonical save pointer is malformed');
  }
  return {
    version: 1,
    chronologyId: candidate.chronologyId,
    namespace: candidate.namespace as Continuity['namespace'],
    checkpointId: candidate.checkpointId,
    through: candidate.through as number,
    nextSequence: candidate.nextSequence as number,
    updatedAtMs: typeof candidate.updatedAtMs === 'number' ? candidate.updatedAtMs : 0,
  };
}

/**
 * One canonical rolling save: a single pointer to one checksummed checkpoint plus the
 * write-ahead journal of accepted operations. There is no player-selectable save slot.
 */
export class DurableRunStore<S extends RollingState = RunState, O = DurableOperation> {
  readonly #driver: StorageDriver;
  readonly #chronologyId: string;
  readonly #namespace: Continuity['namespace'];
  readonly #holderId: string;
  readonly #now: () => number;
  readonly #interrupt: (boundary: WriteBoundary) => void;
  #lock: SingleWriterLock | null = null;
  #pointer: Pointer | null = null;
  #state: S | null = null;
  /** The model hash of #state, kept with it: a commit's digestBefore is the last commit's digestAfter. */
  #digest = '';
  readonly #model: RollingModel<S, O>;
  readonly #snapshotEvery: number;
  readonly #leaseTtlMs: number;
  readonly #onLeaseWait: ((wait: LeaseWait) => void) | undefined;
  #queue: Promise<unknown> = Promise.resolve();
  #closed = false;
  #interrupted = false;
  #paused: StorageUnavailableError | null = null;

  private constructor(options: DurableStoreOptions<S, O>) {
    this.#driver = options.driver;
    this.#model = options.model ?? benchModel as unknown as RollingModel<S, O>;
    this.#snapshotEvery = options.snapshotEvery ?? 128;
    this.#leaseTtlMs = options.leaseTtlMs ?? 15_000;
    this.#onLeaseWait = options.onLeaseWait;
    if (!Number.isSafeInteger(this.#snapshotEvery) || this.#snapshotEvery < 1) throw new Error('Snapshot interval must be a positive integer');
    this.#chronologyId = options.chronologyId;
    this.#namespace = options.namespace;
    this.#holderId = options.holderId;
    this.#now = options.now ?? (() => Date.now());
    this.#interrupt = boundary => {
      try { options.interrupt?.(boundary); } catch (error) { this.#interrupted = true; throw error; }
    };
  }

  static open<S extends RollingState, O>(options: DurableStoreOptions<S, O> & { model: RollingModel<S, O> }): Promise<DurableRunStore<S, O>>;
  static open(options: DurableStoreOptions): Promise<DurableRunStore>;
  static async open<S extends RollingState = RunState, O = DurableOperation>(options: DurableStoreOptions<S, O>): Promise<DurableRunStore<S, O>> {
    const store = new DurableRunStore(options);
    await store.#start(options.leaseTtlMs ?? 15_000);
    return store;
  }

  get paused(): StorageUnavailableError | null { return this.#paused; }
  get chronologyId(): string { return this.#chronologyId; }
  get holderId(): string { return this.#holderId; }

  get state(): S {
    if (!this.#state) throw new SaveCorruptError('This rolling save has not been loaded yet');
    return structuredClone(this.#state);
  }

  /** Load one checkpoint and its journal from the same storage read snapshot. */
  load(create?: () => S): Promise<S> { return this.#serialize(() => this.#load(create)); }

  async #load(create?: () => S): Promise<S> {
    if (this.#state) return this.state;
    const saved = await this.#driver.read(snapshot => ({
      pointer: snapshot.get('meta', POINTER_KEY), checkpoints: snapshot.all('checkpoints'), journal: snapshot.all('journal'),
    }));
    if (saved.pointer === undefined) {
      const state = create?.();
      if (!state) throw new SaveCorruptError('This chronology has no rolling save yet and none was offered');
      await this.#begin(this.#model.parse(state));
      return this.state;
    }
    const pointer = parsePointer(saved.pointer);
    if (pointer.chronologyId !== this.#chronologyId) throw new SaveCorruptError('The rolling save belongs to another chronology');
    if (pointer.namespace !== this.#namespace) throw new SaveCorruptError(`This is a ${pointer.namespace} save; it cannot be opened as ${this.#namespace}`);
    const stored = saved.checkpoints.find(entry => entry.key === pointer.checkpointId);
    if (!stored) throw new SaveCorruptError('The canonical pointer names a checkpoint that is not present');
    const checkpoint = decodeCheckpoint(stored.value, this.#model);
    if (checkpoint.chronologyId !== this.#chronologyId || checkpoint.state.envelope.namespace !== this.#namespace) throw new SaveCorruptError('The rolling checkpoint belongs to another chronology or namespace');
    if (checkpoint.through !== pointer.through) throw new SaveCorruptError('The canonical pointer and its checkpoint disagree on the journal boundary');
    const journal = saved.journal.filter(entry => keySequence(entry.key) >= pointer.through).sort((a, b) => keySequence(a.key) - keySequence(b.key));
    const records = validateJournal(journal.map(entry => entry.value), checkpoint.through, this.#model.parseOperation);
    if (pointer.nextSequence !== checkpoint.through + records.length) throw new SaveCorruptError('The canonical pointer and the rolling journal disagree on how much play is recorded');
    let state = checkpoint.state;
    for (const record of records) {
      if (this.#model.hash(state) !== record.digestBefore) throw new SaveCorruptError(`The rolling journal chain does not hold at sequence ${record.sequence}`);
      state = this.#model.apply(state, record.operation);
      if (this.#model.hash(state) !== record.digestAfter) throw new SaveCorruptError(`Sequence ${record.sequence} does not reproduce its recorded outcome`);
    }
    this.#pointer = pointer;
    if (this.#model.upgrade) {
      const upgraded = this.#model.parse(this.#model.upgrade(structuredClone(state)));
      if (this.#model.hash(upgraded) !== this.#model.hash(state)) {
        const checkpoint = encodeCheckpoint(upgraded, pointer.nextSequence, this.#model);
        const next = { ...pointer, checkpointId: checkpoint.id, through: pointer.nextSequence, updatedAtMs: this.#now() };
        await this.#transaction(async tx => {
          await this.#assertWriter(tx);
          await tx.put('checkpoints', checkpoint.id, checkpoint);
          await tx.put('meta', POINTER_KEY, next);
          await this.#prune(tx, next);
        });
        this.#pointer = next;
        state = upgraded;
      }
    }
    this.#state = state; this.#digest = this.#model.hash(state);
    return this.state;
  }

  /** Legacy bench entry points; playable hosts use commit through createRunStore. */
  acceptIntent(input: unknown): Promise<S> {
    const intent: AcceptedIntent = AcceptedIntentSchema.parse(input);
    return this.commit({ kind: 'intent', intent } as O, 'intent');
  }
  commitDeath(personId: string, eventId: string): Promise<S> {
    return this.commit({ kind: 'death', personId, eventId } as O, 'death', true);
  }

  /** Reduction, journal, snapshot and pointer are one serialized consequence. The reduction may yield between steps. */
  commit(operation: O | ((state: S) => O | Promise<O>), prefix: 'intent' | 'death' = 'intent', snapshot = false): Promise<S> {
    return this.#serialize(async () => this.#append(typeof operation === 'function' ? await (operation as (state: S) => O | Promise<O>)(this.state) : operation, prefix, snapshot));
  }

  rotateCheckpoint(): Promise<void> { return this.#serialize(() => this.#rotateCheckpoint()); }

  async #rotateCheckpoint(): Promise<void> {
    const pointer = this.#requirePointer();
    const checkpoint = encodeCheckpoint(this.state, pointer.nextSequence, this.#model);
    // Retain the old two-stage rotation contract; orphan checkpoints are never authority.
    await this.#transaction(async tx => {
      await this.#assertWriter(tx);
      await tx.put('checkpoints', checkpoint.id, checkpoint);
    });
    this.#interrupt('rotate:after-checkpoint');
    const next = { ...pointer, checkpointId: checkpoint.id, through: pointer.nextSequence, updatedAtMs: this.#now() };
    await this.#transaction(async tx => {
      await this.#assertWriter(tx);
      await tx.put('meta', POINTER_KEY, next);
      await this.#prune(tx, next);
    });
    this.#pointer = next;
    this.#interrupt('rotate:after-commit');
  }

  /** A paused or idle host can renew without advancing fiction. Every commit also renews. */
  heartbeat(): Promise<void> { return this.#serialize(() => this.#transaction(async tx => { await this.#assertWriter(tx); })); }

  /** Retry durable storage after it refused a write; the canonical state never moved. */
  retry(): Promise<void> { return this.#serialize(() => this.#retry(), true); }

  async #retry(): Promise<void> {
    if (!this.#paused) return;
    try {
      await this.#driver.write(async tx => { await this.#assertWriter(tx); await tx.put('meta', 'retry-probe', { at: this.#now() }); await tx.remove('meta', 'retry-probe'); });
    } catch (error) {
      if (isQuotaError(error)) throw new StorageUnavailableError(this.#state?.tick ?? 0, 'the browser still has no storage space for the rolling save');
      throw error;
    }
    this.#paused = null;
  }

  async close(): Promise<void> {
    await this.#queue;
    if (this.#closed) return;
    this.#closed = true;
    if (!this.#lock) { this.#driver.close(); return; }
    try { await this.#lock.release(); } finally { this.#lock = null; this.#driver.close(); }
  }

  async #start(leaseTtlMs: number): Promise<void> {
    await this.#driver.open(schema => { for (const store of STORES) schema.createStore(store); });
    this.#lock = new SingleWriterLock(this.#driver, this.#chronologyId, this.#holderId, { now: this.#now, ttlMs: leaseTtlMs });
    try {
      await this.#acquireLease(leaseTtlMs);
    } catch (error) {
      this.#driver.close();
      this.#lock = null;
      if (isQuotaError(error)) throw new StorageUnavailableError(0, 'the browser has no storage space for the rolling save. Free some space, then retry.');
      throw error;
    }
  }

  /** A window closed mid-teardown keeps its lease until it lapses. Reopening at once therefore waits
   * at most one lease lifetime for that lapse instead of landing on a lock error; a window that is
   * still renewing, or a canon chronology, is refused immediately because no wait can free it. */
  async #acquireLease(leaseTtlMs: number): Promise<void> {
    const first = await this.#attemptLease();
    if (!first) return;
    const waitMs = this.#leaseWaitMs(first, leaseTtlMs);
    if (waitMs === null) throw first;
    this.#onLeaseWait?.({ waitMs, holderId: first.holderId });
    await sleep(waitMs);
    const second = await this.#attemptLease();
    if (second) throw second;
  }

  async #attemptLease(): Promise<WriterConflictError | null> {
    try { await this.#lock!.acquire(); return null; }
    catch (error) { if (error instanceof WriterConflictError) return error; throw error; }
  }

  /** Null keeps the refusal: only a lease that lapses within one lifetime of this clock is worth waiting for. */
  #leaseWaitMs(conflict: WriterConflictError, leaseTtlMs: number): number | null {
    if (conflict.retryAtMs === null) return null;
    const remaining = conflict.retryAtMs - this.#now();
    if (remaining > leaseTtlMs) return null;
    return Math.max(remaining, 0) + LEASE_WAIT_MARGIN_MS;
  }

  async #begin(state: S): Promise<void> {
    if (state.envelope.chronologyId !== this.#chronologyId || state.envelope.namespace !== this.#namespace) {
      throw new SaveCorruptError('A new rolling save must belong to this chronology and namespace');
    }
    const checkpoint = encodeCheckpoint(state, 0, this.#model);
    const pointer: Pointer = { version: 1, chronologyId: this.#chronologyId, namespace: this.#namespace, checkpointId: checkpoint.id, through: 0, nextSequence: 0, updatedAtMs: this.#now() };
    this.#interrupt('begin:before-write');
    await this.#transaction(async tx => {
      this.#interrupt('begin:in-transaction');
      await this.#assertWriter(tx);
      await tx.put('checkpoints', checkpoint.id, checkpoint);
      await tx.put('meta', POINTER_KEY, pointer);
    });
    this.#interrupt('begin:after-write');
    this.#pointer = pointer;
    this.#state = state; this.#digest = checkpoint.digest;
  }

  async #append(raw: O, prefix: 'intent' | 'death', snapshot: boolean): Promise<S> {
    await nextTask();
    const pointer = this.#requirePointer();
    const operation = this.#model.parseOperation(raw);
    await nextTask();
    const before = this.state;
    const applied = this.#model.apply(before, operation);
    await nextTask();
    const state = this.#model.parse(applied);
    await nextTask();
    const record: JournalRecord<O> = { sequence: pointer.nextSequence, digestBefore: this.#digest, digestAfter: this.#model.hash(state), operation };
    await nextTask();
    const next = { ...pointer, nextSequence: record.sequence + 1, updatedAtMs: this.#now() };
    const checkpoint = snapshot || next.nextSequence - pointer.through >= this.#snapshotEvery
      ? encodeCheckpoint(state, next.nextSequence, this.#model) : null;
    if (checkpoint) { next.checkpointId = checkpoint.id; next.through = next.nextSequence; }
    this.#interrupt(`${prefix}:before-write`);
    await this.#transaction(async tx => {
      this.#interrupt(`${prefix}:in-transaction`);
      await this.#assertWriter(tx);
      await tx.put('journal', journalKey(record.sequence), record);
      if (checkpoint) await tx.put('checkpoints', checkpoint.id, checkpoint);
      await tx.put('meta', POINTER_KEY, next);
      if (checkpoint) await this.#prune(tx, next);
    });
    this.#pointer = next;
    this.#state = state; this.#digest = record.digestAfter;
    this.#interrupt(`${prefix}:after-write`);
    return this.state;
  }

  async #transaction(body: (tx: StorageTransaction) => Promise<void>): Promise<void> {
    if (this.#paused) throw this.#paused;
    try {
      await this.#driver.write(body);
    } catch (error) {
      if (isQuotaError(error)) {
        this.#paused = new StorageUnavailableError(this.#state?.tick ?? 0, 'the browser has no storage space left for the rolling save. Free some space, then retry.');
        throw this.#paused;
      }
      throw error;
    }
  }

  async #assertWriter(tx: StorageTransaction): Promise<void> {
    const lease = parseLease(await tx.get('owner', LEASE_KEY));
    const token = this.#lock?.token ?? 0;
    if (!lease || lease.chronologyId !== this.#chronologyId || lease.holderId !== this.#holderId || lease.fencingToken !== token) {
      throw new StaleWriterError(`This tab (${this.#holderId}) no longer holds the rolling save, so it cannot advance the run. Reload to recover the canonical state.`);
    }
    const saved = await tx.get('meta', POINTER_KEY);
    if ((this.#pointer === null && saved !== undefined) || (this.#pointer !== null && canonical(saved) !== canonical(this.#pointer))) {
      throw new StaleWriterError('The canonical save changed. Reload before advancing this life.');
    }
    await tx.put('owner', LEASE_KEY, { ...lease, expiresAtMs: this.#now() + this.#leaseTtlMs });
  }

  async #prune(tx: StorageTransaction, pointer: Pointer): Promise<void> {
    for (const entry of await tx.all('journal')) if (keySequence(entry.key) < pointer.through) await tx.remove('journal', entry.key);
    for (const entry of await tx.all('checkpoints')) if (entry.key !== pointer.checkpointId) await tx.remove('checkpoints', entry.key);
  }

  #serialize<T>(work: () => Promise<T>, retry = false): Promise<T> {
    const run = this.#queue.then(() => {
      if (this.#closed || this.#interrupted) throw new StaleWriterError('This save connection is closed or interrupted. Reload to recover the canonical life.');
      if (this.#paused && !retry) throw this.#paused;
      return work();
    });
    this.#queue = run.catch(() => undefined);
    return run;
  }

  #requirePointer(): Pointer {
    if (!this.#pointer) throw new SaveCorruptError('This rolling save has not been loaded yet');
    return this.#pointer;
  }
}
